import React, { useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Phone, MapPin, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { submitToPabbly } from '@/utils/pabblyConnect';

const Contact = () => {
  const { toast } = useToast();
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    services: [] as string[],
    specificService: '',
    message: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const services = [
    { id: 'web-design', label: 'Website Design' },
    { id: 'photography', label: 'Photography' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'seo', label: 'SEO Services' },
    { id: 'branding', label: 'Branding' },
    { id: 'software', label: 'Software' },
    { id: 'real-estate', label: 'Real Estate Media' },
    { id: 'tech-coaching', label: 'Tech Coaching' },
    { id: 'virtual-tours', label: 'Virtual Tours' },
    { id: 'led-printing', label: 'LED Printing' }
  ];

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // Handle checkbox changes
  const handleServiceChange = (serviceId: string, checked: boolean) => {
    setFormData((prev) => {
      if (checked) {
        return { ...prev, services: [...prev.services, serviceId] };
      } else {
        return { ...prev, services: prev.services.filter(id => id !== serviceId) };
      }
    });
  };

  // Handle consent checkbox change
  const handleConsentChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, consent: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast({
        title: "Consent required",
        description: "Please agree to the terms and privacy policy to proceed.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit to Pabbly Connect
      await submitToPabbly(formData, 'contact_page_form');
      
      // Reset form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        phone: '',
        city: '',
        state: '',
        services: [],
        specificService: '',
        message: '',
        consent: false
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout 
      title="Contact Us | Astrem" 
      description="Get in touch with us to discuss your project needs, request a quote, or learn more about our services."
    >
      <PageHero 
        title="Contact Us" 
        subtitle="Get in Touch"
        description="Have a project in mind or questions about our services? We'd love to hear from you. Fill out the form below, and we'll get back to you as soon as possible."
        backgroundImage="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=1000&auto=format&fit=crop"
      />

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Contact Info
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Let's Discuss Your Project
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  Whether you're looking for a new website, professional photography, marketing assistance, or any of our other services, we're here to help. Fill out the contact form, and we'll get back to you promptly.
                </p>
                
                <div className="space-y-6 mb-8">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Phone className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium mb-1">Phone</h3>
                      <p className="text-astrem-gray-600">
                        <a href="tel:+12255725736" className="hover:text-primary transition-colors">
                          (225) 572-5736
                        </a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium mb-1">Location</h3>
                      <p className="text-astrem-gray-600">
                        Baton Rouge, Louisiana
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Clock className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium mb-1">Business Hours</h3>
                      <p className="text-astrem-gray-600">
                        Monday - Friday: 9am - 5pm CST
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="relative h-80 rounded-lg overflow-hidden">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d110133.34260316578!2d-91.20139726562497!3d30.45105880000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x86243867325f74cb%3A0x2123f1db91579a1d!2sBaton%20Rouge%2C%20LA!5e0!3m2!1sen!2sus!4v1649350922854!5m2!1sen!2sus" 
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="bg-white rounded-lg shadow-lg p-8 border border-astrem-gray-200">
                <h3 className="text-2xl font-medium mb-6">Send Us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input 
                        id="firstName" 
                        placeholder="John" 
                        value={formData.firstName}
                        onChange={handleChange}
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input 
                        id="lastName" 
                        placeholder="Doe" 
                        value={formData.lastName}
                        onChange={handleChange}
                        required 
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company">Company</Label>
                    <Input 
                      id="company" 
                      placeholder="Company Name (Optional)" 
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="john@example.com" 
                        value={formData.email}
                        onChange={handleChange}
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input 
                        id="phone" 
                        type="tel" 
                        placeholder="(555) 555-5555" 
                        value={formData.phone}
                        onChange={handleChange}
                        required 
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input 
                        id="city" 
                        placeholder="Your City" 
                        value={formData.city}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input 
                        id="state" 
                        placeholder="Your State" 
                        value={formData.state}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <Label>Select Services *</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {services.map((service) => (
                        <div key={service.id} className="flex items-center space-x-2">
                          <Checkbox 
                            id={service.id} 
                            checked={formData.services.includes(service.id)}
                            onCheckedChange={(checked) => 
                              handleServiceChange(service.id, checked as boolean)
                            }
                          />
                          <Label htmlFor={service.id} className="text-sm cursor-pointer">
                            {service.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="specificService">Specific Service Needed</Label>
                    <Input 
                      id="specificService" 
                      placeholder="Example: Portrait Photography, Food Photography, Contractor Website, etc." 
                      value={formData.specificService}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea 
                      id="message" 
                      placeholder="Tell us about your project or inquiry..." 
                      className="h-32"
                      value={formData.message}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-start space-x-2">
                      <Checkbox 
                        id="consent" 
                        checked={formData.consent} 
                        onCheckedChange={handleConsentChange}
                        className="mt-1"
                      />
                      <label htmlFor="consent" className="text-sm text-astrem-gray-700">
                        <span dangerouslySetInnerHTML={{ __html: `By checking this box, you agree to Astrem's <a class="tp text-astrem-black hover:underline" href="/terms" target="_blank">Terms of Service</a> and <a class="tp text-astrem-black hover:underline" href="/privacy-policy" target="_blank">Privacy Policy</a>. You also consent to receive text messages from Astrem. Message frequency may vary but is generally kept to a minimum. Standard messages and data rates may apply. For HELP, text us at 225-331-6114 or email admin@astrem.co. To unsubscribe, send STOP.<br><br>` }} />
                      </label>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full rounded-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Common Questions
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Frequently Asked Questions</h2>
              <p className="text-lg text-astrem-gray-600">
                Find quick answers to some of our most commonly asked questions. If you don't see what you're looking for, feel free to contact us directly.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <ScrollReveal delay={100}>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-astrem-gray-200">
                <h3 className="text-xl font-medium mb-3">What is your typical response time?</h3>
                <p className="text-astrem-gray-600">
                  We respond to most inquiries within 24 business hours. For urgent matters, please indicate this in your message, and we'll prioritize your request.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-astrem-gray-200">
                <h3 className="text-xl font-medium mb-3">Do you work with clients outside of Baton Rouge?</h3>
                <p className="text-astrem-gray-600">
                  Yes! While we're based in Baton Rouge, we work with clients nationwide. Many of our services can be provided remotely, and we're also available for travel when necessary.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-astrem-gray-200">
                <h3 className="text-xl font-medium mb-3">How do your pricing and payment terms work?</h3>
                <p className="text-astrem-gray-600">
                  Pricing varies based on the specific service and project requirements. After our initial consultation, we'll provide a detailed proposal with transparent pricing. We typically require a deposit to begin work, with the balance due upon completion.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={400}>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-astrem-gray-200">
                <h3 className="text-xl font-medium mb-3">What's your project process like?</h3>
                <p className="text-astrem-gray-600">
                  Our process begins with a consultation to understand your needs, followed by a proposal. Once approved, we move into planning, execution, and review phases. We maintain clear communication throughout and ensure you're satisfied with the final deliverables.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Contact;
